import { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Loader2, CheckCircle } from 'lucide-react';
import { getAnalyticsSessionId, trackEvent } from '../lib/analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATIC_HEADER = { cta_url: "/donate" };
const STATIC_FOOTER = { contact_email: "info@securethevotemd.com" };

const ContactPage = () => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', message: '', terms_accepted: false });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!form.terms_accepted) {
      setError('Please accept the terms to continue.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await trackEvent('form_submit', '/contact-us', null, { metadata: { form_type: 'contact' } });
      await axios.post(`${API_URL}/api/contact/submit`, { ...form, session_id: getAnalyticsSessionId() });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <TopBar />
      <Header config={STATIC_HEADER} />

      {/* Hero Section */}
      <div className="relative pt-[50px]">
        <div
          className="relative py-20 sm:py-28"
          style={{
            backgroundImage: `url('https://securethevotemd.com/images/2025/03/2025-Feb-25-Secure-the-Vorte-76-scaled.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          <div className="absolute inset-0" style={{ backgroundColor: 'rgba(158, 27, 50, 0.75)' }} />
          <div className="relative z-10 text-center">
            <motion.h1
              className="text-4xl sm:text-5xl font-bold text-white mb-4"
              style={{ fontFamily: "'Playfair Display', serif" }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              data-testid="contact-hero-title"
            >
              Send Us A Message
            </motion.h1>
            <motion.div
              className="w-16 h-1 bg-white mx-auto mb-6"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            />
            <motion.p
              className="text-white/85 text-lg mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              We would love to hear from you.
            </motion.p>
            <motion.a
              href="/donate"
              className="inline-block bg-[#9E1B32] text-white px-8 py-3 font-bold text-sm tracking-wider hover:bg-[#7a1630] transition-colors"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              DONATE NOW
            </motion.a>
          </div>
        </div>
      </div>

      {/* Contact Form */}
      <main className="py-12 sm:py-16">
        <div className="container mx-auto px-4 max-w-xl">
          {submitted ? (
            <motion.div
              className="text-center py-12"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="contact-success"
            >
              <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Thank You!</h2>
              <p className="text-slate-600">We've received your message and will be in touch shortly.</p>
            </motion.div>
          ) : (
            <motion.form
              onSubmit={handleSubmit}
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              data-testid="contact-form"
            >
              <div className="space-y-2">
                <Label className="text-slate-800 font-semibold">First Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                  placeholder="First Name"
                  className="h-12 border-slate-300"
                  data-testid="contact-first-name"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-800 font-semibold">Last Name <span className="text-red-500">*</span></Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  placeholder="Last Name"
                  className="h-12 border-slate-300"
                  data-testid="contact-last-name"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-800 font-semibold">Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Email"
                  className="h-12 border-slate-300"
                  data-testid="contact-email"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-800 font-semibold">Phone <span className="text-red-500">*</span></Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Phone"
                  className="h-12 border-slate-300"
                  data-testid="contact-phone"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-800 font-semibold">Message</Label>
                <Textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="How can we help?"
                  className="min-h-[140px] border-slate-300"
                  data-testid="contact-message"
                />
              </div>

              <div className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  checked={form.terms_accepted}
                  onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })}
                  className="mt-1 h-4 w-4 border-slate-300 rounded"
                  data-testid="contact-terms"
                />
                <span className="text-sm text-slate-600 leading-relaxed">
                  I agree to terms &amp; conditions provided by the company. By providing my phone number, I agree to receive text messages from the business.
                </span>
              </div>

              {error && (
                <p className="text-red-500 text-sm" data-testid="contact-error">{error}</p>
              )}

              <div className="pt-2">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto px-16 h-12 text-base font-bold tracking-wider"
                  style={{ backgroundColor: '#F6BF58', color: '#fff' }}
                  data-testid="contact-submit"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : 'SUBMIT'}
                </Button>
              </div>
            </motion.form>
          )}
        </div>
      </main>

      <Footer config={STATIC_FOOTER} />
    </div>
  );
};

export default ContactPage;

